import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckSquare, Wrench, ArrowRight, Shield, Users } from 'lucide-react';
import { DataScope } from '@/types';

interface SearchResultGroupProps {
  title: string;
  type: 'calendar' | 'tasks' | 'maintenance';
  results: any[];
  query: string;
  onSelect: (item: any) => void;
  // 🛡️ Hybrid Scoping Props
  activeScope: DataScope;
}

/**
 * 🛰️ SearchResultGroup Component
 * Refactored for Hybrid Scoping: Adds visual markers for Personal vs Shared data origins.
 */
export const SearchResultGroup: React.FC<SearchResultGroupProps> = ({
  title,
  type,
  results,
  query,
  onSelect,
  activeScope
}) => {
  const { t } = useTranslation();

  if (results.length === 0) return null;

  /**
   * 🧠 HIGHLIGHT ENGINE
   */
  const HighlightText = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight.trim()) return <span>{text}</span>;
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, i) => 
          regex.test(part) ? (
            <span key={i} className="text-brand dark:text-indigo-400 font-black underline decoration-indigo-500/30 underline-offset-2">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  const getIcon = () => {
    switch (type) {
      case 'calendar': return <Calendar className="w-3.5 h-3.5" />;
      case 'tasks': return <CheckSquare className="w-3.5 h-3.5" />;
      case 'maintenance': return <Wrench className="w-3.5 h-3.5" />;
    }
  };

  return (
    <div className="mb-6 last:mb-0 animate-in fade-in slide-in-from-top-2 duration-300">
      {/* Group Header */}
      <div className="px-4 mb-2 flex items-center gap-2">
        <div className="text-slate-300 dark:text-slate-600">{getIcon()}</div>
        <h3 className="text-2xs font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
          {title}
        </h3>
        <div className="h-px flex-1 bg-slate-50 dark:bg-slate-900 ml-2" />
      </div>

      {/* Result Items */}
      <div className="space-y-1">
        {results.map((item) => {
          // 🛡️ Determine the source scope for visual branding
          const isPersonal = item.scope === 'personal';

          return (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="w-full group flex items-center justify-between p-4 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-all border border-transparent hover:border-slate-100 dark:hover:border-slate-800"
            >
              <div className="flex items-start gap-3 min-w-0">
                {/* 🛡️ SOURCE ATTRIBUTION BADGE: Critical for Google Data Privacy Compliance */}
                <div className={`mt-1 p-1 rounded-md border ${
                  isPersonal 
                    ? 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 text-emerald-500' 
                    : 'bg-indigo-50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-800 text-brand'
                }`}>
                  {isPersonal ? <Shield className="w-2.5 h-2.5" /> : <Users className="w-2.5 h-2.5" />}
                </div>

                <div className="flex flex-col text-left min-w-0">
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">
                    <HighlightText text={item.title || item.assetName} highlight={query} />
                  </span>
                  {item.description || item.protocolName ? (
                    <span className="text-2xs font-medium text-slate-400 dark:text-slate-500 truncate mt-0.5">
                      <HighlightText 
                        text={item.description || item.protocolName} 
                        highlight={query} 
                      />
                    </span>
                  ) : null}
                  
                  {/* 🛡️ Partition Indicator Label */}
                  <span className={`text-[7px] font-black uppercase tracking-widest mt-1 ${
                    isPersonal ? 'text-emerald-500/70' : 'text-brand/70'
                  }`}>
                    {isPersonal ? t('common.private', 'Private Partition') : t('common.shared', 'Shared Hub')}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {item.start && (
                  <span className="text-[9px] font-black text-slate-300 dark:text-slate-700 uppercase tabular-nums">
                    {new Date(item.start).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </span>
                )}
                <ArrowRight className="w-4 h-4 text-slate-200 group-hover:text-brand group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};